import parsePhoneNumberFromString, {
    isValidPhoneNumber,
    type CountryCode,
} from 'libphonenumber-js/max';
import { SupportedCountryCodes } from 'src/constants/SupportedCountryCodes';

const getEnvironment = () => import.meta.env.VITE_ENV;

interface ValidationRuleOptions {
    isRequired?: boolean;
    requiredErrorMessage?: string;
    invalidErrorMessage?: string;
}

const validateIsTenDigitPhoneNumber = (value: string, countryCode = '+1') => {
    // Strip out country code
    const noCountryCode = value.replace(countryCode, '');
    const onlyDigits = noCountryCode.replace(/\D/g, '');

    return onlyDigits.length === 10;
};

const validatePhoneNumberWithSupportedCountries = (value: string): boolean => {
    try {
        if (!isValidPhoneNumber(value)) {
            return false;
        }

        // Parse the phone number to get the country code
        const phoneNumber = parsePhoneNumberFromString(value);
        const countryCode = phoneNumber?.country as CountryCode;

        // Check if the country code is in our supported list
        return SupportedCountryCodes.includes(countryCode);
    } catch (error) {
        // If parsing fails, it's not a valid phone number
        return false;
    }
};

export const getPhoneNumberValidationRules = (options?: ValidationRuleOptions) => {
    const {
        isRequired = false,
        requiredErrorMessage = 'Phone number is required',
        invalidErrorMessage = 'Phone number is invalid',
    } = options || {};
    if (isRequired) {
        return {
            validate: (value: string) => {
                if (!value || value === '+1') {
                    return requiredErrorMessage;
                } else if (getEnvironment() !== 'production') {
                    return validateIsTenDigitPhoneNumber(value) ? true : invalidErrorMessage;
                } else {
                    return validatePhoneNumberWithSupportedCountries(value)
                        ? true
                        : invalidErrorMessage;
                }
            },
        };
    } else {
        if (getEnvironment() !== 'production') {
            return {
                validate: (value: string) =>
                    !value || // empty string is allowed
                    value === '+1' || // MuiTelInput default value
                    validateIsTenDigitPhoneNumber(value)
                        ? true
                        : invalidErrorMessage,
            };
        } else {
            return {
                validate: (value: string) =>
                    !value || // empty string is allowed
                    value === '+1' || // MuiTelInput default value
                    validatePhoneNumberWithSupportedCountries(value)
                        ? true
                        : invalidErrorMessage,
            };
        }
    }
};

// Limit the length of the phone number to 11 digits (10 digits + 1 country code)
export const limitPhoneNumberLength = (value: string, maxLength = 11) => {
    const onlyDigits = value.replace(/\D/g, '');

    if (onlyDigits.length > maxLength) {
        return value.slice(0, value.length - (onlyDigits.length - maxLength));
    }
    return value;
};
